import {
  ArtifactPageType,
  Custom_Encode_URL,
  DocumentCategories,
  IArtifact,
  LogoSize,
  PinTypes,
  documentTypeMappings,
  ResourceTabs,
  newDocumentCategories,
} from 'app/types/Shared';
import smallLogo from 'assets/images/header/elevateLogoSmall.svg';
import takedaLogo from 'assets/images/header/takedaLogo.svg';
import GoverningStandardProcessIcon from 'assets/images/ArtifactIcons/GoverningStandardProcess.svg';
import KeyProcessGxPIcon from 'assets/images/ArtifactIcons/keyProcessGxP.svg';
import KeyProcessBusinessControlledIcon from 'assets/images/ArtifactIcons/keyProcessBusinessControlled.svg';
import ProcessGear from 'assets/images/ArtifactIcons/ProcessGear.svg';

/////React Router Dom/////
import {NavLink, useNavigate} from 'react-router-dom';
///Local Components//////
import PageLayout from 'components/PageLayout/PageLayout';
import TopNavigation from 'components/TopNavigation/TopNavigation';
import ChangeRole from 'components/ChangeRole/ChangeRole';
import {Breadcrumbs, Link, Grid, Typography, Stack, useTheme, Box} from '@mui/material';
import {PinFilled} from '@carbon/icons-react';
// import {SearchFilter} from 'app/store/search/searchSlice';
import React from 'react';
import ArtifactPageUI from 'components/ArtifactsPage/ArtifactPageUI';
import {useSelector} from 'react-redux';
import {selectAllArtifacts, selectMasterSOP} from 'app/store/artifacts/artifactSlice';
import {selectOnboarding} from 'app/store/appState/appStateSlice';
import {useMatomo} from '@jonkoops/matomo-tracker-react';
import {selectAllProcesses} from 'app/store/operatingModel/operatingModelSlice';
import DocumentFilters from 'components/DocumentFilters/DocumentFilters';
import {ArtifactSearch} from 'components/ArtifactsPage/SearchBar';

type ArtifactsPageProps = {
  type: ArtifactPageType;
};

const ArtifactsPage = ({type}: ArtifactsPageProps) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const allArtifacts = useSelector(selectAllArtifacts);
  const onboarding = useSelector(selectOnboarding);
  const allProcesses = useSelector(selectAllProcesses);
  const masterSOP = useSelector(selectMasterSOP);
  const [activeFilters, setActiveFilter] = React.useState<Array<DocumentCategories>>([]);
  const [avalibleFilters, setAvalibleFilters] = React.useState<Array<DocumentCategories>>(
    Object.values(DocumentCategories),
  );
  const [currentTab, setCurrentTab] = React.useState<ResourceTabs>(ResourceTabs.FutureFit);
  const [currentTabIndex, setCurrentTabIndex] = React.useState(0);

  const onHome = () => {
    navigate(`/`);
    return Promise.resolve();
  };

  const onSearch = (query?: string) => {
    navigate(`/search?q=${query}`);
    return Promise.resolve();
  };

  const onLogout = async () => {
    navigate('/logout');
    return Promise.resolve();
  };

  const toResources = async () => {
    navigate('/resources');
    return Promise.resolve();
  };

  // Matomo Analytics
  const {trackPageView} = useMatomo();
  React.useEffect(() => {
    trackPageView();
  });

  // start at top of page
  const [onFirstLoad, setFirstLoad] = React.useState(true);
  React.useEffect(() => {
    if (onFirstLoad) {
      window.scrollTo(0, 0);
      setFirstLoad(false);
    }
  }, [onFirstLoad]);

  const header = (
    <TopNavigation
      authorized={true}
      logout={onLogout}
      home={onHome}
      smallLogo={smallLogo}
      rightLogo={takedaLogo}
      LogoType={LogoSize.Small}
      search={onSearch}
      toResources={toResources}
      banner={onboarding.topBanner}
    />
  );
  const footer = <></>;

  //add the tab they are currently under to search query
  const handleDocumentSearch = (docQuery: string) => {
    navigate(`/search?q=${docQuery}&tab=${currentTabIndex}`);
  };

  // Returns true if the artifact type matches the active filter
  const getFilter = (type: string) => {
    // Check if 'AllResources' is in the activeFilters array
    // If there are no active filters, all resources are considered included
    if (activeFilters?.length === 0) {
      return true;
    }

    // Map active filters to their corresponding document types and flatten the result
    const includedTypes = activeFilters.flatMap((filter) => documentTypeMappings[filter] || []);

    // If all active filters are included in the document type mappings, return true
    if (activeFilters.every((filter) => documentTypeMappings[filter]?.includes(type))) {
      return true;
    }

    // Check if the type exists in the included types
    return includedTypes.includes(type);
  };

  const getItemKey = () => {
    const iconKey: Array<{title: string; svg: string}> = [];
    if (currentTab !== ResourceTabs.TTDOM) {
      const artifactsCategory = Array.from(
        new Set(allArtifacts?.flatMap((a) => a.j_process_artifact?.flatMap((jpa) => jpa.document_category))),
      );
      if (artifactsCategory?.includes(newDocumentCategories.GoverningStandardProcess)) {
        iconKey.push({title: 'Governing Standard & Process Document', svg: GoverningStandardProcessIcon});
      }
      if (artifactsCategory?.includes(newDocumentCategories.KeyProcessGxP)) {
        iconKey.push({title: 'Key Process GxP Documents', svg: KeyProcessGxPIcon});
      }
      if (artifactsCategory?.includes(newDocumentCategories.KeyProcessBusinessControlled)) {
        iconKey.push({title: 'Key Process Business Controlled Documents', svg: KeyProcessBusinessControlledIcon});
      }
    }
    iconKey.push({title: 'Process', svg: ProcessGear});

    return iconKey;
  };

  // Returns an array of objects with artifacts grouped by process
  const getResultsByProcess = () => {
    const results = allProcesses
      ?.filter(
        (x) =>
          x?.parent_process &&
          ((currentTab === ResourceTabs.TTDOM && x.operating_models_list === 'TTDOM') ||
            (currentTab === ResourceTabs.FutureFit && x.operating_models_list !== 'TTDOM')),
      )
      .reduce((data, curr) => {
        // If the process is in the current tab, add it and any related artifacts to the array
        const processArtifacts = allArtifacts
          ?.filter((artifact) => artifact?.j_process_artifact?.some((x) => x.process.process_id === curr.process_id))
          .filter((artifact) => getFilter(artifact.type))
          .map((artifact) => artifact);

        // If there are artifacts, add the process to the array or if showing process card
        if (
          processArtifacts?.length > 0 ||
          activeFilters?.length === 0 ||
          activeFilters?.includes(DocumentCategories.Processes)
        ) {
          data.push({
            process: {
              id: 0,
              title: `${curr.name}`,
              type: PinTypes.Process,
              created_dt: curr.created_dt,
              key: curr.process_id,
              docid: curr.process_id,
              url: `${window.location.origin}/process/${Custom_Encode_URL(curr.name)}/${curr.operating_models_list}`,
            },
            artifacts: processArtifacts,
          });
        }

        return data;
      }, [] as Array<{process: IArtifact; artifacts: Array<IArtifact>}>);

    //if results is empty, return an empty array

    if (masterSOP && (activeFilters?.length === 0 || activeFilters?.includes(DocumentCategories.SOPs))) {
      results?.unshift({
        process: {
          id: 0,
          title: '',
          type: PinTypes.Process,
          created_dt: masterSOP.created_dt,
          key: 'masterSOP-1234',
          docid: 'masterSOP-1234',
          url: masterSOP.url,
        },
        artifacts: [masterSOP],
      });
    }

    return results ? results : [];
  };

  const handleTabChange = (tab: ResourceTabs, tabIndex: number) => {
    setCurrentTab(tab);
    setCurrentTabIndex(tabIndex);
    setFilters(tab);
  };
  //Set available filters and reset active filters on tab change
  const setFilters = (tab: ResourceTabs) => {
    const processes =
      tab === ResourceTabs.TTDOM
        ? allProcesses?.filter((x) => x?.parent_process && x.operating_models_list === 'TTDOM')
        : allProcesses?.filter((x) => x?.parent_process && x.operating_models_list !== 'TTDOM');
    const filters = [DocumentCategories.Processes];
    if (
      processes?.some((x) =>
        x.artifacts?.some((y) => documentTypeMappings[DocumentCategories.SOPs].includes(y.artifact.type)),
      )
    ) {
      filters.push(DocumentCategories.SOPs);
    }
    if (
      processes?.some((x) =>
        x.artifacts?.some((y) => documentTypeMappings[DocumentCategories.ToolsAndForms].includes(y.artifact.type)),
      )
    ) {
      filters.push(DocumentCategories.ToolsAndForms);
    }
    if (
      processes?.some((x) =>
        x.artifacts?.some((y) => documentTypeMappings[DocumentCategories.BusinessControlled].includes(y.artifact.type)),
      )
    ) {
      filters.push(DocumentCategories.BusinessControlled);
    }
    setAvalibleFilters(filters);
    setActiveFilter([]);
  };

  const availableTabs = () => {
    const tabs = [];
    if (allProcesses?.some((x) => x.operating_models_list === 'TTDOM')) {
      tabs.push(ResourceTabs.TTDOM);
    }
    if (allProcesses?.some((x) => x.operating_models_list !== 'TTDOM')) {
      tabs.push(ResourceTabs.FutureFit);
    }
    return tabs;
  };

  return (
    <PageLayout header={header} footer={footer} globalBannerCallback={onLogout}>
      <Grid
        justifyContent='flex-start'
        alignItems='flex-end'
        container
        direction='row'
        spacing={1}
        px={6}
        marginTop={onboarding?.topBanner ? 7.5 : 2.5}
      >
        <Grid item xs={3}>
          <Breadcrumbs
            sx={{
              '.MuiBreadcrumbs-separator': {
                color: 'primary.main',
              },
            }}
          >
            <Link underline='hover' color='primary.main' component={NavLink} to='/home'>
              Home
            </Link>
            <Link underline='hover' color='primary.main' component={NavLink} to='/resources'>
              Resources
            </Link>
            <Typography color='primary.main'>{type}</Typography>
          </Breadcrumbs>
        </Grid>
        <Grid item xs={9} minHeight='70px' display='flex' justifyContent='flex-end'>
          <ChangeRole />
        </Grid>
      </Grid>
      <Stack spacing={2} px={6} pt={2} pb={4}>
        <Stack direction='row' spacing={2} alignItems='flex-end'>
          <Typography variant='h1'>{type}</Typography>
          {type === ArtifactPageType.Pins && <PinFilled fill={theme.palette.primary.main} />}
        </Stack>
        <ArtifactSearch handleDocumentSearch={handleDocumentSearch} />
      </Stack>
      <Box>
        <DocumentFilters
          filters={avalibleFilters}
          activeFilters={activeFilters}
          currentTabIndex={currentTabIndex}
          onTabChange={handleTabChange}
          onFilter={setActiveFilter}
          tabs={availableTabs()}
        />
        <ArtifactPageUI
          type={type}
          results={getResultsByProcess()}
          activeFilters={activeFilters}
          currentTab={currentTab}
          iconKey={getItemKey()}
        />
      </Box>
    </PageLayout>
  );
};

export default ArtifactsPage;
